import styled, { css } from 'styled-components';

/* Same pane width as the flow's steps, so the simulator sits in the same column. */
export const Wrapper = styled.section`
  font-family: 'Inter', sans-serif;
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  padding: 32px 24px;

  @media (min-width: 768px) {
    padding: 40px 24px;
  }

  /* SidebarBox is shared with the flow steps and ChangePaymentMethod, so the dark
     card is applied here by descent rather than to the component. */
  > section {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    padding: 28px 32px;
    color: #e5e5e5;

    @media (max-width: 576px) {
      padding: 24px 20px;
    }
  }

  /* The flow's CTA purple, flat rather than the shared variant's gradient. */
  button[type='submit'] {
    background: #5968db !important;
    border-color: #5968db !important;
    border-radius: 8px !important;
  }

  button[type='submit']:hover:not(:disabled) {
    background: #4a58c4 !important;
    border-color: #4a58c4 !important;
  }
`;

export const TitleSection = styled.section`
  margin-bottom: 32px;
`;

export const Title = styled.h2`
  ${({ theme }) => css`
    font-size: 3.2rem;
    line-height: 3.8rem;
    font-weight: 600;
    color: #e5e5e5;
    margin-bottom: 8px;

    @media (max-width: 992px) {
      font-size: 2.4rem;
      line-height: 1.23;
    }
  `}
`;

export const Description = styled.p`
  ${({ theme }) => css`
    font-size: 1.6rem;
    line-height: 2.6rem;
    color: #e5e5e5;
    margin: 0;
  `}
`;

export const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: end;
  margin-bottom: 24px;

  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }

  @media (min-width: 576px) and (max-width: 849px) {
    grid-template-columns: 1fr 1fr;
  }

  /* Referral takes the widest share: "Referral code (optional)" is the longest
     label in the row and wrapped onto two lines, which left the row taller than it
     needed to be and the label reading as two fields. Vehicle price and months give
     up the width for it — both hold short values and do not need it.

     The pane is 768px now, matching the flow's steps; the original 2fr/2fr split was
     sized for the wider General column this page used to sit in. */
  @media (min-width: 850px) {
    grid-template-columns: 1.2fr 0.9fr 76px 2fr auto;
  }
`;

export const Field = styled.div`
  ${() => css`
    display: flex;
    flex-direction: column;
    gap: 6px;
    /* Grid tracks default to min-width: auto, so a number input's intrinsic width
       becomes a floor the column cannot go below — which is what pushed the button
       past the card edge rather than shrinking the fields. */
    min-width: 0;

    label {
      font-size: 1.3rem;
      font-weight: 600;
      color: #e5e5e5;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      /* The columns are sized to hold these on one line; wrapping would make the
         row taller and read as two fields. */
      white-space: nowrap;
    }

    input,
    select {
      width: 100%;
      min-width: 0;
      padding: 12px 14px;
      border: 1.5px solid rgba(255, 255, 255, 0.14);
      border-radius: 8px;
      font-size: 1.5rem;
      color: #ffffff;
      background-color: #2b2a3a;
      transition: border-color 0.2s ease;

      &:focus {
        outline: none;
        border-color: #5968db;
      }
    }
  `}
`;

export const ErrorBox = styled.div`
  ${({ theme }) => css`
    padding: 14px 16px;
    border: 1px solid rgba(255, 154, 154, 0.45);
    background: rgba(255, 154, 154, 0.1);
    color: #ff9a9a;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 1.4rem;
  `}
`;

export const WarningBox = styled.div`
  ${({ theme }) => css`
    padding: 14px 16px;
    border: 1px solid rgba(245, 197, 24, 0.45);
    background: rgba(245, 197, 24, 0.1);
    color: #f5c518;
    border-radius: 8px;
    margin-top: 12px;
    font-size: 1.4rem;
  `}
`;

export const SharedRows = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: 20px 24px;
    background: rgba(0, 0, 0, 0.22);
    border-radius: 12px;
    margin-bottom: 20px;
  `}
`;

export const Row = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 1.5rem;

    &:last-child {
      border-bottom: 0;
    }

    span {
      color: #e5e5e5;
    }

    strong {
      color: #ffffff;
      font-weight: 600;
    }
  `}
`;

export const Badge = styled.span`
  ${({ theme }) => css`
    align-self: flex-start;
    display: inline-block;
    padding: 6px 12px;
    background: rgba(126, 226, 168, 0.12);
    color: #7ee2a8;
    border-radius: 999px;
    font-size: 1.2rem;
    font-weight: 600;
    margin-top: 12px;
  `}
`;

export const Cards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  ${({ theme }) => css`
    padding: 24px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
  `}
`;

export const CardTitle = styled.h3`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 12px;
    color: rgba(255, 255, 255, 0.55);
  `}
`;

export const BigAmount = styled.div`
  ${({ theme }) => css`
    font-size: 3.2rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 16px;
    line-height: 1.1;
  `}
`;

export const Small = styled.span`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.55);
    margin-left: 6px;
  `}
`;

export const ShareRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: 12px;
    align-items: stretch;

    input {
      flex: 1;
      /* Same reason as the form fields: a flex item will not shrink below its
         intrinsic width, and the share URL is long. */
      min-width: 0;
      padding: 12px 14px;
      border: 1.5px solid rgba(255, 255, 255, 0.14);
      border-radius: 8px;
      font-size: 1.3rem;
      background: rgba(0, 0, 0, 0.22);
      color: #e5e5e5;
    }

    /* Two buttons share the row now, so they take a little less each and the URL
       field gives up the rest — it only has to be readable, not complete. */
    > button {
      flex: none;
      min-width: 104px;
      display: flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
    }

    @media (max-width: 576px) {
      flex-direction: column;
    }
  `}
`;
